import { useCallback } from 'react';

/**
 * Converts a dotted/camelCase API error key into a human-readable label.
 * e.g. "images.logo" → "Logo image", "headerFields.label" → "Header fields label"
 */
const humaniseErrorKey = (key: string): string => {
  const isImage = key.startsWith('images.');
  const stripped = isImage ? key.replace(/^images\./, '') : key;
  const words = stripped
    .split('.')
    .flatMap((p) => p.replace(/([a-z])([A-Z])/g, '$1 $2').split(' '))
    .map((w) => w.toLowerCase());
  if (isImage) words.push('image');
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(' ');
};

/**
 * Combines a humanised field label with the API error message,
 * avoiding duplication of the trailing label word.
 */
const formatErrorMessage = (key: string, msg: string): string => {
  // `base` is a record-level error not tied to a field; its message is a
  // complete sentence and should be shown without a field label.
  if (key === 'base') return msg;

  const label = humaniseErrorKey(key);
  const lastWord = label.split(' ').pop()!;
  const stripped = msg.replace(new RegExp(`^${lastWord}\\s+`, 'i'), '');
  const firstWord = stripped.split(' ')[0];
  const isAcronym =
    firstWord.length > 1 && firstWord === firstWord.toUpperCase();
  const body = isAcronym
    ? stripped
    : stripped.charAt(0).toLowerCase() + stripped.slice(1);
  return `${label} ${body}`;
};

/**
 * Extracts human-readable error messages from an API error response body.
 * Handles the `{ errors: [{ field: ["msg", …] }] }` format used by PassEntry-API,
 * the `{ errors: ["msg"] }` variant its entitlement handler returns, and falls
 * back to plain string `errors`, `error`, or `message` fields.
 */
export const extractErrorMessages = (
  errorData: Record<string, unknown>
): string[] => {
  if (Array.isArray(errorData.errors)) {
    const messages = errorData.errors.flatMap((entry: unknown) => {
      if (typeof entry === 'string') return [entry];
      if (entry === null || typeof entry !== 'object') return [];
      return Object.entries(entry as Record<string, unknown>).flatMap(
        ([key, msgs]) =>
          (Array.isArray(msgs) ? msgs : [msgs])
            .filter((msg): msg is string => typeof msg === 'string')
            .map((msg) => formatErrorMessage(key, msg))
      );
    });
    if (messages.length > 0) return messages;
  }
  for (const k of ['errors', 'error', 'message'] as const) {
    if (typeof errorData[k] === 'string') return [errorData[k] as string];
  }
  return [];
};

/**
 * Pulls out only the record-level (`base`) messages — complete sentences not
 * tied to a field, so a caller can present them on their own.
 */
export const extractBaseErrorMessages = (
  errorData: Record<string, unknown>
): string[] => {
  if (!Array.isArray(errorData.errors)) return [];
  return errorData.errors.flatMap((entry: unknown) => {
    if (entry === null || typeof entry !== 'object') return [];
    const msgs = (entry as Record<string, unknown>).base;
    if (msgs === undefined) return [];
    return (Array.isArray(msgs) ? msgs : [msgs]).filter(
      (msg): msg is string => typeof msg === 'string'
    );
  });
};

/**
 * Hook that returns functions to extract human-readable error messages
 * from RTK Query / API error responses.
 */
export const useApiErrorMessages = () => {
  const errorBody = (error: unknown) =>
    (error as { data?: Record<string, unknown> })?.data;

  const getErrorMessages = useCallback((error: unknown): string[] => {
    const errorData = errorBody(error);
    if (!errorData) return [];
    return extractErrorMessages(errorData);
  }, []);

  const getBaseErrorMessages = useCallback((error: unknown): string[] => {
    const errorData = errorBody(error);
    if (!errorData) return [];
    return extractBaseErrorMessages(errorData);
  }, []);

  return { getErrorMessages, getBaseErrorMessages };
};
